import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MapPin, Calendar, Sun, Waves, ArrowDown } from 'lucide-react';
import { Countdown } from './components/Countdown';
import { RsvpForm } from './components/RsvpForm';
export function App() {
  const {
    scrollY
  } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 40
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-100px'
    },
    transition: {
      duration: 1.2,
      ease: 'easeOut'
    }
  };
  return <div className="min-h-screen bg-[#FDFBF7] text-[#444] font-sans overflow-x-hidden selection:bg-[#D4735E] selection:text-white" data-id="element-0">
      {/* Global Texture Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-50 bg-[url('https://www.transparenttextures.com/patterns/stucco.png')]" data-id="element-1"></div>

      {/* Hero Section */}
      <header className="relative min-h-screen flex flex-col items-center justify-center p-6 text-center overflow-hidden" data-id="element-2">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0" data-id="element-3">
          <div className="absolute top-0 left-0 w-full h-full bg-[#F5E6D3]/30" data-id="element-4"></div>
          {/* Abstract Sun */}

          {/* Abstract Sea */}
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-[#4A90E2]/10 to-transparent" data-id="element-5"></div>
        </div>

        {/* Main Content */}
        <div className="z-10 relative space-y-6 max-w-4xl mx-auto -mt-20" data-id="element-6">
          {/* Couple Illustration - Now First */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.2,
          ease: 'easeOut'
        }} className="flex justify-center mb-5" data-id="element-7">
            <img src="https://cdn.magicpatterns.com/uploads/gim3Lz7mqUxyiCCVesjEn8/a2af0816-81ba-4b87-8cf3-90e762574626.png" alt="David and Avesta" className="w-64 md:w-80 h-auto opacity-90 mt-[-30px] mb-[-30px]" data-id="element-8" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 1.5,
          ease: 'easeOut'
        }} className="flex flex-col items-center gap-4" data-id="element-9">
            <span className="text-[#7e2625] uppercase tracking-[0.4em] text-sm md:text-base font-medium" style={{
            color: '#555555'
          }} data-id="element-10">
              Bröllop
            </span>

            <h1 className="font-script text-7xl md:text-[9rem] text-[#7e2625] leading-[0.9] drop-shadow-sm rotate-[-2deg] mix-blend-multiply" style={{
            fontFamily: 'Dancing Script, cursive',
            fontSize: '96px'
          }} data-id="element-11">
              David <span className="text-[#7e2625]" data-id="element-12">&</span> Avesta
            </h1>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.8,
          duration: 1.5
        }} className="flex md:flex-row md:gap-12 text-xl md:text-2xl font-serif text-[#7e2625] mt-12 pt-[10px] pb-[10px] gap-[5px] flex-col items-center justify-center" data-id="element-13">
            <div className="flex items-center gap-2" data-id="element-14">
              <Calendar size={24} className="text-[#7E2625]" data-id="element-15" />
              <span style={{
              fontSize: '24px',
              color: '#555555'
            }} data-id="element-16">
                August 22, 2026
              </span>
            </div>
            <span className="hidden md:block w-2 h-2 rounded-full bg-[#F5E6D3]" data-id="element-17"></span>
            <div className="flex items-center gap-2" data-id="element-18">
              <MapPin size={24} className="text-[#7E2625]" data-id="element-19" />
              <span style={{
              color: '#555555'
            }} data-id="element-20">
                Beckershofs Herrgård
              </span>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div style={{
        opacity
      }} animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-12 text-[#D4735E]/60" data-id="element-21">
          <ArrowDown size={32} strokeWidth={1.5} data-id="element-22" />
        </motion.div>
      </header>

      {/* Countdown Section */}
      <section className="py-24 px-6 relative bg-[#F5E6D3]/20" data-id="element-23">
        <div className="max-w-5xl mx-auto text-center space-y-16" data-id="element-24">
          <motion.div {...fadeIn} className="space-y-6" data-id="element-25">
            <Sun className="w-12 h-12 mx-auto text-[#7E2625] animate-spin-slow" style={{
            animationDuration: '20s'
          }} data-id="element-26" />
            <h2 className="text-4xl md:text-6xl font-serif text-[#444]" style={{
            color: '#7E2625'
          }} data-id="element-27">
              Nedräkning
            </h2>
            <p className="text-[#7C8B5F] text-lg max-w-lg mx-auto leading-relaxed" style={{
            color: '#555555'
          }} data-id="element-28">
              We're counting every second until we can celebrate under the
              Mediterranean sky with you.
            </p>
          </motion.div>

          <Countdown data-id="element-29" />
        </div>
      </section>

      {/* Location Section */}
      <section className="py-32 px-6 relative overflow-hidden" data-id="element-30">
        {/* Decorative background shape */}

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-31">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-32">
            {/* Image Placeholder with Organic Shape */}
            <motion.div {...fadeIn} className="relative aspect-[4/5] bg-[#F5E6D3] shadow-2xl overflow-hidden" style={{
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%'
          }} data-id="element-33">
              <div className="absolute inset-0 bg-gradient-to-br from-[#4A90E2]/20 to-[#D4735E]/20 mix-blend-multiply" data-id="element-34"></div>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-35">
                <Waves className="w-24 h-24 text-white opacity-40" data-id="element-36" />
              </div>
              {/* Texture */}
              <div className="absolute inset-0 opacity-12 bg-[url('https://cdn.magicpatterns.com/uploads/vZRa2fLh3KiAcwD5zwJxQK/DJI_20240803082731_0022_D-scaled.webp')] bg-cover bg-center" data-id="element-37"></div>
            </motion.div>

            {/* Details Card */}
            <motion.div {...fadeIn} transition={{
            delay: 0.2,
            duration: 1.2
          }} className="bg-white p-10 md:p-12 shadow-xl relative" style={{
            borderRadius: '20px 40px 20px 60px'
          }} data-id="element-38">
              <div className="space-y-8" data-id="element-39">
                <div className="space-y-3" data-id="element-40">
                  <span className="text-[#4A90E2] font-bold tracking-widest uppercase text-xs" style={{
                  color: '#7E2625'
                }} data-id="element-41">
                    The Venue
                  </span>
                  <h2 className="text-4xl md:text-5xl font-serif text-[#7e2625] leading-tight" data-id="element-42">
                    Beckershofs Herrgård
                  </h2>
                </div>

                <div className="space-y-8 text-[#555]" data-id="element-43">
                  <p className="text-lg leading-relaxed" data-id="element-44">
                    Beckershofs Herrgård ligger 15 mil sydväst om Stockholm,
                    några kilometer söder om Katrineholm. Herrgården är belägen
                    på en udde vid sjön Tisnaren i en naturskön del av Sörmland.
                  </p>

                  <div className="space-y-6 border-l-2 border-[#F5E6D3] pl-6" data-id="element-45">
                    <div data-id="element-46">
                      <h3 className="font-serif text-xl text-[#7C8B5F] mb-1" style={{
                      color: '#7E2625',
                      fontWeight: '700'
                    }} data-id="element-47">
                        Vigsel
                      </h3>
                      <p data-id="element-48">16:00 — På udden bakom huvudbyggnaden</p>
                    </div>
                    <div data-id="element-49">
                      <h3 className="font-serif text-xl text-[#7C8B5F] mb-1" style={{
                      color: '#7E2625',
                      fontWeight: '700'
                    }} data-id="element-50">
                        Middag
                      </h3>
                      <p data-id="element-51">18:00 PM — Orangeriet</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6" data-id="element-52">
                  <a href="#" className="inline-flex items-center gap-2 text-[#7e2625] font-medium hover:text-[#4A90E2] transition-colors border-b border-[#D4735E] pb-1" data-id="element-53">
                    <MapPin size={18} data-id="element-54" />
                    <span data-id="element-55">View Map & Directions</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* RSVP Section */}
      <section className="py-24 px-6 relative bg-[#D4735E]/5" data-id="element-56">
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-57">
          <motion.div {...fadeIn} className="text-center mb-16 space-y-6" data-id="element-58">
            <h2 className="text-4xl md:text-6xl font-serif text-[#7e2625]" data-id="element-59">
              OSA
            </h2>
            <p className="text-[#7C8B5F] text-lg" style={{
            color: '#555555'
          }} data-id="element-60">
              Vänligen meddela oss om din närvaro innan 1 juni, 2026
            </p>
          </motion.div>

          <RsvpForm data-id="element-61" />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 text-center bg-[#FDFBF7] border-t border-[#F5E6D3]" data-id="element-62">
        <div className="max-w-md mx-auto px-6 space-y-8" data-id="element-63">
          <div className="w-16 h-1 bg-[#D4735E] mx-auto rounded-full opacity-40" data-id="element-64"></div>
          <h3 className="font-script text-4xl text-[#444]" style={{
          fontFamily: 'Dancing Script, cursive'
        }} data-id="element-65">
            David & Avesta
          </h3>
          <div className="flex justify-center gap-8 text-[#7C8B5F] text-sm font-medium tracking-widest uppercase" data-id="element-66">
            <a href="#" className="hover:text-[#D4735E] transition-colors" data-id="element-67">
              Registry
            </a>
            <a href="#" className="hover:text-[#D4735E] transition-colors" data-id="element-68">
              Travel
            </a>
            <a href="#" className="hover:text-[#D4735E] transition-colors" data-id="element-69">
              Contact
            </a>
          </div>
          <p className="text-xs text-[#999] pt-8" data-id="element-70">
            © 2026 — With love from the Amalfi Coast
          </p>
        </div>
      </footer>

      {/* Font Import */}
      <style data-id="element-71">{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');
        
        .font-script {
          font-family: 'Dancing Script', cursive;
        }
        .font-serif {
          font-family: 'Crimson Text', serif;
        }
      `}</style>
    </div>;
}